import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext"; 
import { toast } from "sonner";
import { 
  ArrowRight, 
  Eye, 
  EyeOff, 
  User, 
  Mail, 
  Lock, 
  Sparkles, 
  Building2, 
  Check, 
  CircleDot 
} from "lucide-react";
import BrandHeader from "@/components/BrandHeader"; 
import ResendVerificationBox from "@/components/auth/ResendVerificationBox";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Live password requirements checklist
  const criteria = [
    { id: "length", label: "Minimum 6 characters", met: password.length >= 6 },
    { id: "upper", label: "At least one uppercase letter (A-Z)", met: /[A-Z]/.test(password) },
    { id: "number", label: "At least one number (0-9)", met: /[0-9]/.test(password) },
    { id: "special", label: "At least one special character (!@#$...)", met: /[^A-Za-z0-9]/.test(password) },
  ];

  const allCriteriaMet = criteria.every((c) => c.met);

  const getPasswordStrength = () => {
    const metCount = criteria.filter((c) => c.met).length;
    if (!password) return { level: 0, label: "", colorClass: "bg-slate-200", textClass: "" };
    if (metCount <= 2) {
      return { level: 1, label: "Weak", colorClass: "bg-amber-500", textClass: "text-amber-500" };
    }
    if (metCount === 3) {
      return { level: 2, label: "Medium", colorClass: "bg-blue-600", textClass: "text-blue-600 font-bold" };
    }
    return { level: 3, label: "Strong", colorClass: "bg-emerald-500", textClass: "text-emerald-600 font-black" };
  };

  const strengthInfo = getPasswordStrength();

  const submit = async (e) => {
    e.preventDefault();
    if (!allCriteriaMet) {
      toast.error("Please fulfill all password requirements before submitting.");
      return;
    }
    setLoading(true);
    try {
      const res = await register(name.trim(), email.trim(), password);
      toast.success(res?.message || "Signup successful! Please check your email to verify your account.");
      setIsSuccess(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 font-sans selection:bg-blue-200 flex flex-col justify-between relative overflow-x-hidden">
      <div className="absolute top-10 left-10 w-96 h-96 bg-blue-500/15 rounded-full blur-[100px] pointer-events-none"></div>
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-indigo-500/15 rounded-full blur-[100px] pointer-events-none"></div>

      <div className="relative w-full bg-gradient-to-r from-blue-700 via-indigo-800 to-slate-900 text-white pt-8 pb-32 px-6 rounded-b-[3.5rem] shadow-2xl overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#fff_1px,transparent_1px)] [background-size:16px_16px]"></div>
        <div className="absolute -top-24 -right-24 w-80 h-80 bg-blue-500/30 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-5xl mx-auto flex items-center justify-between mb-8 relative z-10">
          <div className="[&_*]:!text-white [&_*]:!fill-white [&_*]:!border-white/30 brightness-125">
            <BrandHeader size="lg" />
          </div>

        </div>

        <div className="text-center max-w-xl mx-auto relative z-10">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/30 border border-blue-400/30 text-blue-200 text-xs font-bold uppercase tracking-widest mb-3 backdrop-blur-md shadow-sm">
      
            <span>Exhibition Lead Engagement</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight leading-none drop-shadow-md">
            Capture every <span className="text-blue-400 underline decoration-white decoration-2">conversation</span>.
          </h1>
          <p className="text-blue-100/80 mt-2.5 text-sm sm:text-base font-medium max-w-md mx-auto">
            Digitize your exhibition leads and trigger automated follow-ups instantly.
          </p>
        </div>
      </div>

      <main className="max-w-md w-full mx-auto -mt-20 px-4 relative z-20 pb-12 flex-grow">
        <div className="bg-white/90 backdrop-blur-2xl border border-white shadow-2xl shadow-indigo-950/10 rounded-[2.5rem] p-6 sm:p-10 relative overflow-hidden">
          
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-500"></div>

          {isSuccess ? (
            <ResendVerificationBox
              email={email}
              onProceedLogin={() => navigate("/auth/login")}
            />
          ) : (
            <>
              <div className="grid grid-cols-2 p-1.5 bg-slate-100/80 rounded-2xl mb-8 border border-slate-200/60 shadow-inner">
                <Link 
                  to="/auth/login" 
                  data-testid="goto-login"
                  className="py-2.5 text-center text-xs font-bold text-gray-500 rounded-xl hover:text-gray-900 transition-all block"
                >
                  LOGIN
                </Link>
                <div className="py-2.5 text-center text-xs font-black bg-white text-blue-600 rounded-xl shadow-md border border-gray-100/80 cursor-default">
                  SIGN UP
                </div>
              </div>

              <form onSubmit={submit} className="flex flex-col gap-4" data-testid="register-form">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-700 ml-1">
                    Full name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                      <User className="w-5 h-5" />
                    </div>
                    <input
                      required
                      className="ls-input w-full pr-4 py-3.5 bg-slate-50/70 border border-slate-200 rounded-2xl text-sm font-semibold text-gray-900 placeholder-gray-400 focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10 transition-all outline-none"
                      style={{ paddingLeft: '2.75rem' }}
                      placeholder="Alex Rivera"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      data-testid="register-name-input"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-700 ml-1">
                    Email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                      <Mail className="w-5 h-5" />
                    </div>
                    <input
                      type="email"
                      required
                      className="ls-input w-full pr-4 py-3.5 bg-slate-50/70 border border-slate-200 rounded-2xl text-sm font-semibold text-gray-900 placeholder-gray-400 focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10 transition-all outline-none"
                      style={{ paddingLeft: '2.75rem' }}
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="register-email-input"
                    />
                  </div>
                </div>
                
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-700 ml-1">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                      <Lock className="w-5 h-5" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      required
                      className="ls-input w-full py-3.5 bg-slate-50/70 border border-slate-200 rounded-2xl text-sm font-semibold text-gray-900 placeholder-gray-400 focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10 transition-all outline-none"
                      style={{ paddingLeft: '2.75rem', paddingRight: '3rem' }}
                      placeholder="Create secure password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute p-2 transition-colors -translate-y-1/2 right-2.5 top-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>

                  {/* Password Requirement Checklist */}
                  {password.length > 0 && (
                    <div className="mt-2.5 p-3.5 bg-slate-50 border border-slate-200/80 rounded-2xl space-y-2 animate-in fade-in duration-200">
                      <div className="flex justify-between items-center text-xs font-bold">
                        <span className="text-slate-500">Security Score:</span>
                        <span className={strengthInfo.textClass}>{strengthInfo.label}</span>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-1.5 h-1.5 mb-3">
                        <div className={`rounded-full transition-all duration-300 ${strengthInfo.level >= 1 ? strengthInfo.colorClass : 'bg-slate-200'}`}></div>
                        <div className={`rounded-full transition-all duration-300 ${strengthInfo.level >= 2 ? strengthInfo.colorClass : 'bg-slate-200'}`}></div>
                        <div className={`rounded-full transition-all duration-300 ${strengthInfo.level >= 3 ? strengthInfo.colorClass : 'bg-slate-200'}`}></div>
                      </div>

                      <div className="space-y-1.5 pt-1 border-t border-slate-200/50">
                        {criteria.map((item) => (
                          <div 
                            key={item.id} 
                            className={`flex items-center gap-2 text-xs font-medium transition-colors duration-200 ${
                              item.met ? "text-emerald-700 font-semibold" : "text-slate-500"
                            }`}
                          >
                            {item.met ? (
                              <div className="w-4 h-4 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
                                <Check className="w-2.5 h-2.5 stroke-[3]" />
                              </div>
                            ) : (
                              <div className="w-4 h-4 rounded-full bg-slate-200/70 flex items-center justify-center text-slate-400 shrink-0">
                                <CircleDot className="w-2.5 h-2.5" />
                              </div>
                            )}
                            <span>{item.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !allCriteriaMet}
                  className="h-14 w-full bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 hover:from-blue-700 hover:to-indigo-800 text-white text-base font-black rounded-2xl flex items-center justify-center gap-2 shadow-xl shadow-blue-600/25 hover:shadow-blue-600/35 hover:scale-[1.01] active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:pointer-events-none mt-4 group"
                  data-testid="register-submit-button"
                >
                  {loading ? (
                    "Creating account…"
                  ) : (
                    <>
                      <span>Create account</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </form>
            </>
          )}

        </div>
      </main>
    </div>
  );
}